import type { ShipmentLocationActionRequestDto } from '@uturn/api/v1';
import { useLocationTypeTranslation } from '@uturn/portal/modules/shipments/hooks';
import {
	addValidationIssue,
	type ValidationResults,
	validationSuccess,
} from '@uturn/portal/types/validation';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { FormValues } from '../schema';

export const useLocationDatesValidation = () => {
	const { t } = useTranslation();
	const locationTypeTranslation = useLocationTypeTranslation();
	/**
	 * Validate shipment route dates.
	 *
	 * @logic
	 * On publishing:
	 * - Pickup until >= current time (All dates should be in the future, as you can't drive a shipment in the past)
	 * - Publish until >= current time (to make sure the shipment is actually published)
	 *
	 * * Publish until validation enabled (used in multi-update):
	 * - Publish until <= Pickup until
	 *
	 * Shipment date/time location validation common logic:
	 * - Location from  <= Next location from
	 * - Location until <= Next location until
	 * - Location from  <= Same location until
	 *
	 * On other updates logic:
	 * - No check on date in the future (because shipment can we partly executed in the past)
	 *
	 * @param locations
	 * @param publishDateUntil
	 * @param publishing Publishing or updating published shipment
	 * @param skipPublishUntilValidation Publish until is not in play: the shipment
	 *                                   stays a concept, or the field is read-only
	 * @param validationResults
	 * @returns
	 */
	const validateLocationDates = (
		locations: ShipmentLocationActionRequestDto[],
		publishDateUntil: string,
		publishing: boolean,
		skipPublishUntilValidation: boolean = false,
		validationResults?: ValidationResults<FormValues>,
	): ValidationResults<FormValues> => {
		let results = validationResults ?? validationSuccess;

		const pickupDateUntil = locations[0]?.dateUntil;

		if (publishing) {
			if (pickupDateUntil && dayjs(pickupDateUntil).isBefore(dayjs())) {
				results = addValidationIssue<FormValues>(
					results,
					t(
						'general.errors.shipment.location_dates.pickup_date_until_invalid',
						'Must be in the future',
					),
					'locations.0.action.dateUntil',
				);
			}
		}

		if (!skipPublishUntilValidation) {
			// The API rejects a past "Publish until" on publishing and on every
			// update of a published shipment.
			if (publishDateUntil && dayjs(publishDateUntil).isBefore(dayjs())) {
				results = addValidationIssue<FormValues>(
					results,
					t(
						'general.errors.shipment.location_dates.publish_date_until_invalid',
						'Must be in the future',
					),
					'publishUntilDate',
				);
			}

			// Enforce "Publish until" must be on or before "Pick-up until".
			if (
				publishDateUntil &&
				pickupDateUntil &&
				dayjs(pickupDateUntil).isBefore(dayjs(publishDateUntil))
			) {
				results = addValidationIssue<FormValues>(
					results,
					t(
						'general.errors.shipment.location_dates.publish_before_pickup_until_invalid',
						'Publish until must be on or before pick-up date until',
					),
					'publishUntilDate',
				);
			}
		}

		locations
			.map((location, index) => ({
				...location,
				sequenceNumber: index,
			}))
			.filter(
				(location) =>
					location.dateFrom &&
					location.dateUntil &&
					new Date(location.dateFrom) > new Date(location.dateUntil),
			)
			.forEach((location) => {
				results = addValidationIssue<FormValues>(
					results,
					t(
						'general.errors.shipment.location_dates.date_until_after_date_from',
						'Must be after date from',
					),
					`locations.${location.sequenceNumber}.action.dateUntil`,
				);
			});

		for (let i = 0; i < locations.length; i += 1) {
			const location = locations[i];
			const nextLocation = locations[i + 1];

			if (!nextLocation) {
				continue;
			}

			if (
				location.dateFrom &&
				nextLocation.dateFrom &&
				new Date(location.dateFrom) > new Date(nextLocation.dateFrom)
			) {
				results = addValidationIssue<FormValues>(
					results,
					t(
						'general.errors.shipment.location_dates.date_from_after_previous_date_from',
						"Must be after the {{locationType}} location's date from",
						{
							locationType:
								locationTypeTranslation[location.locationActionType],
						},
					),
					`locations.${i + 1}.action.dateFrom`,
				);
			}

			if (
				location.dateUntil &&
				nextLocation.dateUntil &&
				new Date(location.dateUntil) > new Date(nextLocation.dateUntil)
			) {
				results = addValidationIssue<FormValues>(
					results,
					t(
						'general.errors.shipment.location_dates.date_until_after_previous_date_until',
						"Must be after the {{locationType}} location's date until",
						{
							locationType:
								locationTypeTranslation[location.locationActionType],
						},
					),
					`locations.${i + 1}.action.dateUntil`,
				);
			}
		}

		return results;
	};
	return { validateLocationDates };
};
